import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronRight, Filter, X, Search, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'Toutes catégories', slug: null },
  { value: 'filtres', label: 'Filtres & Cartouches', slug: 'filtres', description: 'Cartouches de filtration pour une eau pure et saine.' },
  { value: 'pompes', label: 'Pompes', slug: 'pompes', description: 'Pompes de circulation et pompes jets pour votre spa.' },
  { value: 'chauffages', label: 'Chauffages', slug: 'chauffages', description: 'Réchauffeurs et résistances pour maintenir l\'eau chaude.' },
  { value: 'couvertures', label: 'Couvertures', slug: 'couvertures', description: 'Couvertures isothermes pour protéger votre spa.' },
  { value: 'produits-traitement', label: 'Produits de traitement', slug: 'produits-traitement', description: 'Chlore, brome, pH et produits d\'entretien.' },
  { value: 'cartes-electroniques', label: 'Cartes électroniques', slug: 'cartes-electroniques', description: 'Circuits de contrôle et cartes Balboa, Gecko.' },
];

const AccessoiresPage = () => {
  const [accessories, setAccessories] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const response = await axios.get(`${API}/accessories`);
        setAccessories(response.data);
        setFilteredAccessories(response.data);
      } catch (error) {
        console.error('Error fetching accessories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccessories();
  }, []);

  useEffect(() => {
    let result = [...accessories];
    
    if (category !== 'all') {
      result = result.filter(acc => acc.category === category);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(acc => 
        acc.name.toLowerCase().includes(searchLower) ||
        acc.reference.toLowerCase().includes(searchLower) ||
        acc.compatibility.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredAccessories(result);
  }, [category, search, accessories]);

  const clearFilters = () => {
    setCategory('all');
    setSearch('');
  };

  const hasFilters = category !== 'all' || search;

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://atlantiquespaboutique.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Accessoires & Pièces",
        "item": "https://atlantiquespaboutique.com/accessoires"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="accessoires-page">
      <SEO 
        title="Accessoires & Pièces détachées pour Spa"
        description="Filtres, pompes, chauffages, couvertures et pièces détachées pour spas. Stock disponible, livraison France. Bien vérifier les dimensions avant commande."
        canonical="/accessoires"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Accessoires & Pièces</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Accessoires & Pièces détachées
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">
            Filtres, pompes, chauffages, couvertures et pièces techniques. Stock disponible. Bien vérifier les dimensions avant commande.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="w-5 h-5" />
              <span className="font-medium hidden sm:inline">Filtres :</span>
            </div>
            
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher une pièce..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 gap-1">
                <X className="w-4 h-4" />
                Effacer
              </Button>
            )}

            <div className="ml-auto text-sm text-slate-500">
              {filteredAccessories.length} article{filteredAccessories.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Categories quick links - Now with dedicated pages */}
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Parcourir par catégorie</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.slice(1).map(cat => (
              <Link 
                key={cat.value} 
                to={`/accessoires/${cat.slug}`}
                className="group"
              >
                <Card className="h-full hover:border-orange-300 hover:shadow-md transition-all">
                  <CardContent className="p-4 text-center">
                    <h3 className="font-medium text-slate-900 group-hover:text-orange-600 mb-1">
                      {cat.label}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2">{cat.description}</p>
                    <ArrowRight className="w-4 h-4 mx-auto mt-2 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-4 bg-slate-200 rounded mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredAccessories.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-600 mb-4">Aucun article ne correspond à votre recherche.</p>
            <Button onClick={clearFilters}>Voir tous les articles</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAccessories.map((acc) => (
              <Card key={acc.id} className="card-hover border-slate-200 bg-white" data-testid={`accessory-card-${acc.slug}`}>
                <CardContent className="p-5">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                    {categories.find(c => c.value === acc.category)?.label || acc.category}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{acc.name}</h3>
                  <div className="text-sm text-slate-600 mb-3">
                    <div>Réf: {acc.reference}</div>
                    {acc.compatibility && (
                      <div className="text-slate-500">Compatible: {acc.compatibility}</div>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{acc.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    {acc.price_on_request ? (
                      <span className="text-orange-600 font-medium">Sur demande</span>
                    ) : (
                      <span className="text-xl font-bold text-slate-900">{formatPrice(acc.price)}</span>
                    )}
                    {acc.in_stock && (
                      <span className="badge-stock text-xs">En stock</span>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <a href="mailto:contact@atlantiquespaboutique.com" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full gap-1">
                        <Mail className="w-4 h-4" />
                        Question ?
                      </Button>
                    </a>
                    <Link to={`/devis?piece=${acc.slug}`} className="flex-1">
                      <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600">
                        Commander
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Vous ne trouvez pas votre pièce ?
          </h2>
          <p className="text-slate-300 mb-6">
            Contactez-nous avec la référence ou une photo. Notre équipe trouvera la pièce compatible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-slate-800">
                Demande de pièce
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbJsonLd)
      }} />
    </div>
  );
};

export default AccessoiresPage;
